import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  Box, Paper, Typography, TextField, Button, IconButton,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Alert, Fade, Tooltip, Breadcrumbs, Link,
  Popover, Skeleton
} from '@mui/material';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import ListAltIcon from '@mui/icons-material/ListAlt';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import CloseIcon from '@mui/icons-material/Close';
import SaveIcon from '@mui/icons-material/Save';
import AutoAwesomeMosaicIcon from '@mui/icons-material/AutoAwesomeMosaic';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import PageBreadcrumbs from '../../components/common/PageBreadcrumbs';
import ConfirmDeleteDialog from '../../components/common/ConfirmDeleteDialog';
import { getConfigCrudDefinition } from './configCrudDefinitions';

const inputFieldSx = {
  '& .MuiOutlinedInput-root': {
    height: 38,
    minHeight: 38,
    '& .MuiOutlinedInput-input': {
      padding: '10px 12px',
    },
  },
};

const pageHeaderSx = {
  px: 2.25,
  py: 1.5,
  borderBottom: '1px solid #E5E7EB',
  borderLeft: '4px solid #1E88E5',
  display: 'flex',
  backgroundColor: '#fbfdfe',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 2,
  mb: 3,
};

const ConfigRdmWorkspacePage = ({ entityType: entityTypeProp }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { entityType: entityTypeParam, section, mode, itemKey } = useParams();
  const entityType = entityTypeProp || entityTypeParam || 'hcp';
  const definition = getConfigCrudDefinition(entityType, section || 'reference-datasets');
  
  const isEditMode = mode === 'edit';
  const backPath = `/configuration/${entityType}/reference-datasets`;

  const [lookupName, setLookupName] = useState('');
  const [tableData, setTableData] = useState([]);
  const [columns, setColumns] = useState(['Canonical Value']);
  const [localError, setLocalError] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [deleteConfirm, setDeleteConfirm] = useState({ open: false, target: null, type: null });

  // For model (emulating the 3-dots behavior from old Vue code)
  const [popoverAnchorEl, setPopoverAnchorEl] = useState(null);
  const isPopoverOpen = Boolean(popoverAnchorEl);
  const [modalData, setModalData] = useState({ rowIndex: null, col: null, code: '', description: '' });

  // Inline header editing
  const [editingHeader, setEditingHeader] = useState(null);
  const [headerInputValue, setHeaderInputValue] = useState('');

  // Inline cell editing
  const [editingCell, setEditingCell] = useState(null);
  const [cellInputValue, setCellInputValue] = useState('');
  
  const cellInputRef = useRef(null);
  const headerInputRef = useRef(null);

  useEffect(() => {
    if (!definition) return;
    
    setTimeout(() => {
      if (isEditMode) {
        const cached = localStorage.getItem(definition.storageKey);
        let rows = [];
        try {
          rows = cached ? JSON.parse(cached) : [];
        } catch (e) {
          rows = [];
        }
        const currentKey = decodeURIComponent(itemKey || '');
        const matchedRow = rows.find(r => r.name === currentKey) || location.state?.row;
        
        if (matchedRow) {
          setLookupName(matchedRow.name || '');
          if (matchedRow.tableData && matchedRow.tableData.length > 0) {
            setTableData(matchedRow.tableData);
            const allCols = new Set();
            matchedRow.tableData.forEach(row => {
              if (row && typeof row === 'object') {
                Object.keys(row).forEach(key => allCols.add(key));
              }
            });
            const cols = Array.from(allCols);
            const sortedCols = ['Canonical Value', ...cols.filter(c => c !== 'Canonical Value' && c !== 'canonical')];
            setColumns([...new Set(sortedCols)]);
          } else {
             setTableData([]);
             setColumns(['Canonical Value']);
          }
        } else {
          setLocalError('Unable to find lookup type.');
        }
      } else {
        setColumns(['Canonical Value']);
        setTableData([]);
      }
      setIsLoading(false);
    }, 400); // Simulate network load for smoother skeleton transition
  }, [definition, isEditMode, itemKey, location.state]);

  const handleAddRow = () => {
    const newRow = {};
    columns.forEach(col => {
      newRow[col] = { code: '', description: '' };
    });
    setTableData([...tableData, newRow]);
  };

  const handleAddSource = () => {
    const newSource = `Source ${columns.length}`;
    if (columns.includes(newSource)) return;
    setColumns([...columns, newSource]);
    setTableData(prev => prev.map(row => ({
      ...row,
      [newSource]: { code: '', description: '' }
    })));
  };

  const requestDeleteSource = (col, e) => {
    e.stopPropagation();
    if (col === 'Canonical Value') return;
    setDeleteConfirm({ open: true, target: col, type: 'source' });
  };

  const requestDeleteRow = (index) => {
    setDeleteConfirm({ open: true, target: index, type: 'row' });
  };

  const confirmDelete = () => {
    if (deleteConfirm.type === 'source') {
      const colToRemove = deleteConfirm.target;
      setColumns(columns.filter(c => c !== colToRemove));
      setTableData(prev => prev.map(row => {
        const newRow = { ...row };
        delete newRow[colToRemove];
        return newRow;
      }));
    } else if (deleteConfirm.type === 'row') {
      const indexToRemove = deleteConfirm.target;
      setTableData(prev => prev.filter((_, i) => i !== indexToRemove));
    }
    setDeleteConfirm({ open: false, target: null, type: null });
  };

  const onSave = () => {
    if (!lookupName.trim()) {
      setLocalError('Lookup Name is required.');
      return;
    }
    
    const cached = localStorage.getItem(definition.storageKey);
    let rows = [];
    try {
      rows = cached ? JSON.parse(cached) : [];
    } catch {
      rows = [];
    }

    const today = new Date();
    const tomorrow = new Date();
    tomorrow.setDate(today.getDate() + 31);
    
    const formatDate = (date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };

    const nextItem = {
      name: lookupName.trim(),
      startdate: formatDate(today),
      enddate: formatDate(tomorrow),
      sourcevalues: tableData.length,
      unmappedvalues: 0,
      tableData: tableData
    };

    let nextRows = rows;
    if (isEditMode) {
      const currentKey = decodeURIComponent(itemKey || '');
      nextRows = rows.map(r => r.name === currentKey ? nextItem : r);
    } else {
      if (rows.some(r => r.name === nextItem.name)) {
        setLocalError('Lookup Name already exists.');
        return;
      }
      nextRows = [...rows, nextItem];
    }

    localStorage.setItem(definition.storageKey, JSON.stringify(nextRows));
    if (definition.storageVersion) {
      localStorage.setItem(`${definition.storageKey}__version`, definition.storageVersion);
    }
    navigate(backPath);
  };

  // Header Editing
  const startEditingHeader = (col) => {
    if (col === 'Canonical Value') return;
    setEditingHeader(col);
    setHeaderInputValue(col);
    setTimeout(() => headerInputRef.current?.focus(), 50);
  };
  
  const saveHeaderEdit = () => {
    const newColName = headerInputValue.trim();
    if (!newColName || newColName === editingHeader || columns.includes(newColName)) {
      setEditingHeader(null);
      return;
    }
    setColumns(prev => prev.map(c => c === editingHeader ? newColName : c));
    setTableData(prev => prev.map(row => {
      const newRow = { ...row };
      newRow[newColName] = newRow[editingHeader] || { code: '', description: '' };
      delete newRow[editingHeader];
      return newRow;
    }));
    setEditingHeader(null);
  };

  // Cell Editing
  const startEditingCell = (rowIndex, col, value) => {
    setEditingCell({ rowIndex, col });
    setCellInputValue(value);
    setTimeout(() => cellInputRef.current?.focus(), 50);
  };

  const saveCellEdit = () => {
    if (!editingCell) return;
    const { rowIndex, col } = editingCell;
    const newTable = [...tableData];
    if (!newTable[rowIndex]) newTable[rowIndex] = {};
    if (!newTable[rowIndex][col]) newTable[rowIndex][col] = {};
    
    if (col === 'Canonical Value') {
      newTable[rowIndex][col].description = cellInputValue;
    } else {
      newTable[rowIndex][col].code = cellInputValue;
    }
    setTableData(newTable);
    setEditingCell(null);
  };

  // Modal functionalities
  const openCodeModal = (rowIndex, col, e) => {
    e.stopPropagation();
    const cell = tableData[rowIndex][col] || {};
    setModalData({
      rowIndex,
      col,
      code: cell.code || '',
      description: cell.description || ''
    });
    setPopoverAnchorEl(e.currentTarget);
  };

  const handleModalSave = (newDesc, newCode) => {
    const { rowIndex, col } = modalData;
    const newTable = [...tableData];
    // Keep both Code and Description synchronized with Popover logic mappings
    if (!newTable[rowIndex]) newTable[rowIndex] = {};
    if (!newTable[rowIndex][col]) newTable[rowIndex][col] = {};
    
    newTable[rowIndex][col].description = newDesc !== undefined ? newDesc : modalData.description;
    newTable[rowIndex][col].code = newCode !== undefined ? newCode : modalData.code;

    setTableData(newTable);
    // Don't close popover explicitly, it operates silently like Vue
  };
  
  const handlePopoverClose = () => {
    setPopoverAnchorEl(null);
  };

  if (!definition) return <Alert severity="error">Configuration workspace not available.</Alert>;
  
  return (
    <Box sx={{ maxWidth: '100vw', pb: 6, mt: 1 }}>
      
      {/* Top Breadcrumb Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
        <PageBreadcrumbs items={[
          // { label: 'Configuration', path: `/configuration/${entityType}/model` },
          { label: 'Reference Datasets', path: backPath },
          { label: isEditMode ? 'Edit Lookup Type' : 'Create Lookup Type' }
        ]} />
      </Box>


      {/* Main Container Card */}
      <Paper elevation={0} sx={{
        borderRadius: 3,
        border: 'none',
        boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.05), 0 2px 4px -2px rgb(0 0 0 / 0.05)',
        backgroundColor: '#ffffff',
        overflow: 'hidden'
      }}>
        
        {isLoading ? (
          <Box sx={{ p: 4 }}>
            <Skeleton variant="text" width="40%" height={40} />
            <Skeleton variant="rectangular" height={50} sx={{ mt: 3, mb: 4, borderRadius: 1 }} />
            <Skeleton variant="rectangular" height={300} sx={{ borderRadius: 2 }} />
          </Box>
        ) : (
          <Fade in={!isLoading} timeout={600}>
            <Box>
              {/* Card Header */}
              <Box sx={pageHeaderSx}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  {/* <Box sx={{ p: 1, borderRadius: 1.5, backgroundColor: '#eaf4ff', color: '#1E88E5', display: 'flex' }}>
                     <AutoAwesomeMosaicIcon fontSize="small" />
                  </Box> */}
                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1E293B', mb: 0.25, lineHeight: 1.2 }}>
                      {isEditMode ? 'Edit Lookup Type' : 'Create Lookup Type'}
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 500 }}>
                      {isEditMode ? 'Update the details for this record.' : 'Configure and save a new reference dataset.'}
                    </Typography>
                  </Box>
                </Box>
              </Box>

              <Box sx={{ p: 4 }}>
                {/* Form Controls & Action Buttons */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', mb: 4 }}>
                  <Box sx={{ width: 450 }}>
                    <Typography sx={{ mb: 1, fontWeight: 500,  color: '#475569' }}>
                      Lookup Name <Box component="span" sx={{ color: '#ef4444' }}>*</Box>
                    </Typography>
                    <TextField
                      fullWidth
                      size="small"
                      sx={{
                        ...inputFieldSx,
                        backgroundColor: isEditMode ? '#f8fafc' : '#ffffff',
                        '& .MuiOutlinedInput-root': {
                          ...inputFieldSx['& .MuiOutlinedInput-root'],
                          borderRadius: '6px',
                          color: '#0f172a'
                        }
                      }}
                      value={lookupName}
                      onChange={(e) => setLookupName(e.target.value)}
                      disabled={isEditMode}
                    />
                  </Box>
                  
                  {/* Action Buttons */}
                  <Box sx={{ display: 'flex', gap: 1.5 }}>
                    <Button className="action-btn process-btn" 
                      variant="outlined" 
                      onClick={handleAddRow}
                      startIcon={<AddIcon />} 
                      sx={{ 
                        borderRadius: '6px', 
                        textTransform: 'none', 
                        borderColor: '#e2e8f0', 
                        color: '#3b82f6', 
                        py: 0.7, 
                        px: 2,
                        fontWeight: 500,
                        backgroundColor: '#ffffff',
                        '&:hover': { backgroundColor: '#eff6ff', borderColor: '#bfdbfe' }
                      }}
                    >
                      Add Row
                    </Button>
                    <Button className="action-btn process-btn" 
                      variant="outlined" 
                      onClick={handleAddSource} 
                      startIcon={<AddCircleOutlineIcon />} 
                      sx={{ 
                        borderRadius: '6px', 
                        textTransform: 'none', 
                        borderColor: '#e2e8f0', 
                        color: '#6366f1', 
                        py: 0.7, 
                        px: 2,
                        fontWeight: 500,
                        backgroundColor: '#ffffff',
                        '&:hover': { backgroundColor: '#eef2ff', borderColor: '#c7d2fe' }
                      }}
                    >
                      Add Source
                    </Button>
                  </Box>
                </Box>

                <Box sx={{ 
                  borderRadius: '8px', 
                  overflow: 'hidden',
                  mt: 2,
                  pb: 2
                }}>
                  <TableContainer sx={{ maxHeight: 600 }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          {columns.map(col => (
                            <TableCell 
                              key={col} 
                              onClick={() => startEditingHeader(col)}
                              sx={{ 
                                backgroundColor: '#f8fafc', 
                                color: '#475569', 
                                fontWeight: 600, 
                                borderRight: '1px solid #e2e8f0',
                                borderBottom: '2px solid #e2e8f0',
                                minWidth: 200, 
                                padding: '12px 16px',
                                '&:last-child': { borderRight: 'none' },
                                cursor: col === 'Canonical Value' ? 'default' : 'text',
                                position: 'relative',
                                group: 'header'
                              }}
                            >
                              {editingHeader === col ? (
                                <TextField
                                  inputRef={headerInputRef}
                                  size="small"
                                  value={headerInputValue}
                                  onChange={(e) => setHeaderInputValue(e.target.value)}
                                  onBlur={saveHeaderEdit}
                                  onKeyDown={(e) => e.key === 'Enter' && saveHeaderEdit()}
                                  sx={{ 
                                    ...inputFieldSx,
                                    backgroundColor: '#ffffff', 
                                    borderRadius: 1, 
                                    width: '100%',
                                    '& .MuiOutlinedInput-root': {
                                      ...inputFieldSx['& .MuiOutlinedInput-root'],
                                      '& fieldset': { borderColor: '#cbd5e1' },
                                      '&:hover fieldset': { borderColor: '#94a3b8' },
                                    },
                                    '& .MuiInputBase-input': { p: 0.75,  color: '#1e293b' }
                                  }}
                                />
                              ) : (
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                                   <Typography variant="subtitle2" sx={{ fontWeight: 600,  letterSpacing: '0.5px', color: '#475569', textTransform: 'uppercase' }}>
                                     {col}
                                   </Typography>
                                   {col !== 'Canonical Value' && (
                                     <Tooltip title={`Remove ${col} source`} placement="top">
                                       <IconButton 
                                         size="small" 
                                         onClick={(e) => requestDeleteSource(col, e)} 
                                         sx={{ 
                                           p: 0.5, 
                                           color: '#94a3b8', 
                                           '&:hover': { color: '#ef4444', backgroundColor: '#fee2e2' } 
                                         }}
                                       >
                                         <DeleteIcon fontSize="small" />
                                       </IconButton>
                                     </Tooltip>
                                   )}
                                </Box>
                              )}
                            </TableCell>
                          ))}
                          <TableCell sx={{ 
                            backgroundColor: '#f8fafc', 
                            color: '#475569', 
                            fontWeight: 600, 
                            width: 50,
                            p: 0,
                            borderRight: 'none',
                            borderBottom: '2px solid #e2e8f0'
                          }} />
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {tableData.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={columns.length + 1} align="center" sx={{ py: 8, borderBottom: 'none' }}>
                              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', opacity: 0.6 }}>
                                <AutoAwesomeMosaicIcon sx={{  color: '#94a3b8', mb: 2 }} />
                                <Typography sx={{ color: '#475569',  fontWeight: 500 }}>
                                  No mapping records found
                                </Typography>
                                <Typography sx={{ color: '#64748b',  mt: 0.5 }}>
                                  Click "Add Row" to create your first mapping pair.
                                </Typography>
                              </Box>
                            </TableCell>
                          </TableRow>
                        ) : (
                          tableData.map((row, rowIndex) => (
                            <TableRow 
                              key={rowIndex} 
                              hover
                            >
                              {columns.map((col, cellIndex) => {
                                const isEditing = editingCell?.rowIndex === rowIndex && editingCell?.col === col;
                                const cellData = row[col] || {};
                                const displayVal = col === 'Canonical Value' ? cellData.description : cellData.code;
                                
                                return (
                                  <TableCell 
                                    key={col} 
                                    sx={{ 
                                      borderRight: cellIndex === columns.length - 1 ? '1px solid #ccc' : '1px solid #ccc', 
                                      borderBottom: '1px solid #ccc',
                                      borderLeft: cellIndex === 0 ? '1px solid #ccc' : 'none',
                                      p: 0,
                                      position: 'relative'
                                    }}
                                  >
                                     {isEditing ? (
                                       <Box sx={{ p: '8px' }}>
                                         <TextField
                                           inputRef={cellInputRef}
                                           size="small"
                                           value={cellInputValue}
                                           onChange={(e) => setCellInputValue(e.target.value)}
                                           onBlur={saveCellEdit}
                                           onKeyDown={(e) => e.key === 'Enter' && saveCellEdit()}
                                           fullWidth
                                           placeholder="Enter value"
                                           sx={{ 
                                             ...inputFieldSx,
                                             '& .MuiOutlinedInput-root': { 
                                               ...inputFieldSx['& .MuiOutlinedInput-root'],
                                               backgroundColor: '#ffffff',
                                               boxShadow: '0 0 0 2px #3b82f6 inset',
                                               '& fieldset': { border: 'none' } 
                                             },
                                             '& .MuiInputBase-input': { p: '8.5px 12px',  }
                                           }}
                                         />
                                       </Box>
                                     ) : (
                                       <Box 
                                         onClick={(e) => openCodeModal(rowIndex, col, e)}
                                         sx={{ 
                                           display: 'flex', 
                                           alignItems: 'center', 
                                           justifyContent: 'space-between', 
                                           px: 1.5, 
                                           py: 1,
                                           minHeight: 40, 
                                           cursor: 'pointer',
                                           width: '100%',
                                           '&:hover .more-icon': { opacity: 1, color: '#3b82f6' }
                                         }}
                                       >
                                         <Typography sx={{ 
                                            
                                           color: displayVal ? '#424242' : '#cbd5e1',
                                           fontWeight: 400
                                         }}>
                                           {displayVal || '—'}
                                         </Typography>
                                         <IconButton 
                                           className="more-icon"
                                           size="small" 
                                           onClick={(e) => openCodeModal(rowIndex, col, e)}
                                           sx={{ 
                                             opacity: 0.6, 
                                             transition: 'all 0.2s', 
                                             p: 0.5,
                                             ml: 1
                                           }}
                                         >
                                           <MoreVertIcon fontSize="small" />
                                         </IconButton>
                                       </Box>
                                     )}
                                  </TableCell>
                                );
                              })}
                              <TableCell align="center" sx={{ p: 1, borderBottom: '1px solid #ccc', borderRight: '1px solid #ccc', width: 50 }}>
                                <Tooltip title="Delete row" placement="left">
                                  <IconButton 
                                    size="small" 
                                    onClick={() => requestDeleteRow(rowIndex)}
                                    sx={{ 
                                      color: '#94a3b8',
                                      '&:hover': { color: '#ef4444', backgroundColor: '#fef2f2' }
                                    }}
                                  >
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>

              </Box>
              
              {/* Footer Actions */}
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', p: 2, pt: 0, gap: 2 }}>
                <Button className="action-btn cancel-btn" 
                  onClick={() => navigate(backPath)} 
                  sx={{ 
                    minWidth: 80, 
                    px: 2, 
                    py: 0.5, 
                    textTransform: 'none', 
                    fontWeight: 500,
                    
                    borderRadius: '4px', 
                    color: '#1e88e5', 
                    backgroundColor: '#ffffff',
                  }}
                >
                  Cancel
                </Button>
                <Button className="action-btn process-btn" 
                  onClick={onSave} 
                  variant="contained" 
                  sx={{ 
                    minWidth: 80, 
                    px: 2, 
                    py: 0.5, 
                    textTransform: 'none', 
                    fontWeight: 500, 
                    
                    borderRadius: '4px', 
                    backgroundColor: '#1e88e5', 
                    boxShadow: 'none',
                    '&:hover': { backgroundColor: '#1565c0', boxShadow: 'none' }
                  }}
                >
                  {isEditMode ? 'Update' : 'Save'}
                </Button>
              </Box>
            </Box>
          </Fade>
        )}
      </Paper>

      {/* Advanced Edit Popover (Replaces 3-dots behavior) */}
      <Popover
        open={isPopoverOpen}
        anchorEl={popoverAnchorEl}
        onClose={handlePopoverClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{
          sx: { width: 220, p: 2, borderRadius: 2, border: '1px solid #e2e8f0', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)' }
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
          <Typography sx={{ fontWeight: 500,  color: '#1e88e5' }}>
            Edit Details
          </Typography>
          <IconButton size="small" onClick={handlePopoverClose} sx={{ p: 0.25, color: '#475569' }}>
            <CloseIcon sx={{ fontSize: '1rem' }} />
          </IconButton>
        </Box>
        
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          <TextField
             size="small"
             fullWidth
             sx={{ ...inputFieldSx, '& .MuiOutlinedInput-root': { ...inputFieldSx['& .MuiOutlinedInput-root'], borderRadius: 1 } }}
             value={modalData.description}
             onChange={(e) => {
               setModalData(prev => ({...prev, description: e.target.value}));
               handleModalSave(e.target.value, undefined);
             }}
             placeholder="Description"
          />
          <TextField
             size="small"
             fullWidth
             sx={{ ...inputFieldSx, '& .MuiOutlinedInput-root': { ...inputFieldSx['& .MuiOutlinedInput-root'], borderRadius: 1 } }}
             value={modalData.code}
             onChange={(e) => {
               setModalData(prev => ({...prev, code: e.target.value}));
               handleModalSave(undefined, e.target.value);
             }}
             placeholder="Code"
          />
        </Box>
      </Popover>

      <ConfirmDeleteDialog
        open={deleteConfirm.open}
        onClose={() => setDeleteConfirm({ open: false, target: null, type: null })}
        onConfirm={confirmDelete}
        itemName={deleteConfirm.type === 'source' ? `source "${deleteConfirm.target}"` : 'row'}
      />
    </Box>
  );
};

export default ConfigRdmWorkspacePage;
